/**
 * MapLayerPanel — Floating Layers button + dropdown for toggling map overlays.
 *
 * Replaces the old bottom-left MapLayerControl. Positioned adjacent to VoterFilterPanel.
 * Layer state lives in fieldOpsStore (Zustand).
 */

import { Layers } from 'lucide-react';
import { useFieldOpsStore } from '@/stores/fieldOpsStore';
import { Button } from '@/components/ui/button';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import type { DemographicMetric } from './DemographicOverlay';

const METRICS: { value: DemographicMetric; label: string }[] = [
  { value: 'medianIncome', label: 'Median Income' },
  { value: 'renterRate', label: 'Renter Rate' },
  { value: 'collegeRate', label: 'College Educated' },
  { value: 'pctBlack', label: '% Black' },
  { value: 'pctHispanic', label: '% Hispanic' },
];

export function MapLayerPanel() {
  const showVoterPins = useFieldOpsStore((s) => s.showVoterPins);
  const showTurfs = useFieldOpsStore((s) => s.showTurfs);
  const showContactRate = useFieldOpsStore((s) => s.showContactRate);
  const showDemographics = useFieldOpsStore((s) => s.showDemographics);
  const demographicMetric = useFieldOpsStore((s) => s.demographicMetric);
  const showElectionOverlay = useFieldOpsStore((s) => s.showElectionOverlay);
  const showMajorRoads = useFieldOpsStore((s) => s.showMajorRoads);
  const showLiveCanvassers = useFieldOpsStore((s) => s.showLiveCanvassers);

  const toggle = useFieldOpsStore((s) => s.toggle);
  const setDemographicMetric = useFieldOpsStore((s) => s.setDemographicMetric);

  // Controlled open state so the turf walkthrough can drive the dropdown during its filter tour.
  const open = useFieldOpsStore((s) => s.layersPanelOpen);
  const setOpen = useFieldOpsStore((s) => s.setLayersPanelOpen);

  // Highlight button when any layer deviates from defaults
  const hasActiveOverlay = showContactRate || showDemographics || showElectionOverlay || showMajorRoads || !showVoterPins || !showTurfs;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          data-coachmark="map-layers"
          variant="outline"
          size="sm"
          className={cn(
            'gap-2 rounded-full bg-white/95 backdrop-blur-sm shadow-[0_2px_12px_rgba(0,0,0,0.1)] hover:bg-white hover:shadow-md transition-all duration-200',
            hasActiveOverlay && 'ring-2 ring-primary',
          )}
        >
          <Layers className="h-4 w-4" />
          <span className="text-xs">Layers</span>
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-64 p-0"
      >
        <div className="p-3 space-y-3">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Map Layers
          </div>

          {/* Voter Pins */}
          <LayerToggle
            label="Voter Pins"
            checked={showVoterPins}
            onChange={() => toggle('showVoterPins')}
          />

          {/* Turfs */}
          <LayerToggle
            label="Turfs"
            checked={showTurfs}
            onChange={() => toggle('showTurfs')}
          />

          {/* Turf Contact Rate */}
          <LayerToggle
            label="Turf Contact Rate"
            checked={showContactRate}
            onChange={() => toggle('showContactRate')}
            coachmarkId="layer-contact-rate"
          />

          <div className="border-t border-gray-100" />

          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Data Overlays
          </div>

          {/* Census Demographics */}
          <LayerToggle
            label="Census Demographics"
            checked={showDemographics}
            onChange={() => toggle('showDemographics')}
            coachmarkId="layer-demographics"
          />

          {/* Metric radio buttons + legend when demographics active */}
          {showDemographics && (
            <>
              <div className="pl-6 space-y-1.5">
                {METRICS.map(({ value, label }) => (
                  <label key={value} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="demographic-metric"
                      value={value}
                      checked={demographicMetric === value}
                      onChange={() => setDemographicMetric(value)}
                      className="h-3.5 w-3.5 accent-primary"
                    />
                    <span className="text-xs text-gray-700">{label}</span>
                  </label>
                ))}
              </div>
              <div className="pl-6 pt-1">
                <DemographicLegend metric={demographicMetric} />
              </div>
              <p className="pl-6 text-[10px] text-gray-400">
                Zoom in to county level to see data
              </p>
            </>
          )}

          {/* 2024 Election Results */}
          <LayerToggle
            label="2024 Election Results"
            checked={showElectionOverlay}
            onChange={() => toggle('showElectionOverlay')}
            coachmarkId="layer-election"
          />

          {/* Election legend when active */}
          {showElectionOverlay && (
            <div className="pl-6 space-y-1">
              <ElectionLegend />
              <p className="text-[10px] text-gray-400">
                Zoom in to see precinct-level data
              </p>
            </div>
          )}

          <div className="border-t border-gray-100" />

          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Sign & Table Planning
          </div>

          {/* Major Roads (OSM arterials/collectors + state/US routes).
              Legend is rendered as a floating overlay in TurfMap, not here. */}
          <LayerToggle
            label="Major Roads"
            checked={showMajorRoads}
            onChange={() => toggle('showMajorRoads')}
          />

          <div className="border-t border-gray-100" />

          {/* Live Canvassers */}
          <LayerToggle
            label="Live Canvassers"
            checked={showLiveCanvassers}
            onChange={() => toggle('showLiveCanvassers')}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function LayerToggle({
  label,
  checked,
  onChange,
  coachmarkId,
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
  /** Optional data-coachmark target so the turf walkthrough can spotlight this row. */
  coachmarkId?: string;
}) {
  return (
    <label data-coachmark={coachmarkId} className="flex items-center gap-2 cursor-pointer select-none">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="h-4 w-4 rounded border-gray-300 text-primary accent-primary"
      />
      <span className="text-sm font-medium">{label}</span>
    </label>
  );
}

// Must match COLOR_SCALES in DemographicOverlay.tsx
const LEGEND_COLORS: Record<DemographicMetric, string[]> = {
  medianIncome: ['#d73027', '#fc8d59', '#fee08b', '#d9ef8b', '#1a9850'],
  renterRate:   ['#fcfbfd', '#d2d0e7', '#9e9ac8', '#756bb1', '#4a1486'],
  collegeRate:  ['#feedde', '#fdbe85', '#fd8d3c', '#e6550d', '#a63603'],
  pctBlack:     ['#edf8e9', '#bae4b3', '#74c476', '#31a354', '#006d2c'],
  pctHispanic:  ['#fff5eb', '#fdd0a2', '#f16913', '#d94801', '#8c2d04'],
};

const LEGEND_LABELS: Record<DemographicMetric, string> = {
  medianIncome: 'Median Household Income',
  renterRate: 'Renter Rate',
  collegeRate: '% College Educated',
  pctBlack: '% Black Population',
  pctHispanic: '% Hispanic Population',
};

const ELECTION_COLORS = ['#b71c1c', '#ef5350', '#ffcdd2', '#f5f5f5', '#bbdefb', '#42a5f5', '#1565c0'];

function DemographicLegend({ metric }: { metric: DemographicMetric }) {
  const isIncome = metric === 'medianIncome';

  return (
    <div className="space-y-1">
      <div className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">
        {LEGEND_LABELS[metric]}
      </div>
      <div className="flex h-3 rounded-sm overflow-hidden">
        {LEGEND_COLORS[metric].map((color) => (
          <div key={color} className="flex-1" style={{ backgroundColor: color }} />
        ))}
      </div>
      <div className="flex justify-between text-[10px] text-gray-500">
        {isIncome ? (
          <>
            <span>$20k</span>
            <span>$50k</span>
            <span>$100k+</span>
          </>
        ) : (
          <>
            <span>0%</span>
            <span>50%</span>
            <span>75%+</span>
          </>
        )}
      </div>
    </div>
  );
}

function ElectionLegend() {
  return (
    <div className="space-y-1">
      <div className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">
        2024 Presidential Results
      </div>
      <div className="flex h-3 rounded-sm overflow-hidden">
        {ELECTION_COLORS.map((color) => (
          <div key={color} className="flex-1" style={{ backgroundColor: color }} />
        ))}
      </div>
      <div className="flex justify-between text-[10px] text-gray-500">
        <span>R +50</span>
        <span>Even</span>
        <span>D +50</span>
      </div>
    </div>
  );
}

